import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useViewport } from "../../helpers";
import ImageList from "../../components/imageList/ImageList";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Container,
  Button,
  IconButton,
  Typography,
  Backdrop,
} from "@mui/material";
import { List, ViewModule, Add } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import CreateMyDesign from "../../components/createMyDesign/CreateMyDesign";
import { useNavigate } from "react-router-dom/dist";
import FiltersSearch from "../../components/crm/FiltersSearch";
import {
  cleanMyDesigns,
  closeBackdrop,
  closeCreateDesign,
  openCreateDesign,
} from "../../reducers/myDesigns/myDesignsSlice";
import { fetchMyDesigns } from "../../reducers/myDesigns/myDesignsThunks";
import { useSnackbar } from "notistack";
import { crmService } from "../../services/crm.service";
import DownloadIcon from "@mui/icons-material/Download";
import { id } from "date-fns/locale";

const PREFIX = "MyDesigns";

const classes = {
  root: `${PREFIX}-root`,
  updateButton: `${PREFIX}-updateButton`,
  updateButtonContainer: `${PREFIX}-updateButtonContainer`,
  pullToRefresh: `${PREFIX}-pullToRefresh`,
  containerMarginTop: `${PREFIX}-containerMarginTop`,
  uploadingContainer: `${PREFIX}-uploadingContainer`,
  toggleDisplayButtons: `${PREFIX}-toggleDisplayButtons`,
  createMyDesignButton: `${PREFIX}-createMyDesignButton`,
  dataGridMargin: `${PREFIX}-dataGridMargin`,
  actionsStyle: `${PREFIX}-actionsStyle`,
  crmStyle: `${PREFIX}-crmStyle`,
  countCrmYellow: `${PREFIX}-countCrmYellow`,
  countCrmRed: `${PREFIX}-countCrmRed`,
  loadingDesigns: `${PREFIX}-loadingDesigns`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    "& .MuiDataGrid-renderingZone": {
      maxHeight: "none !important",
    },
    "& .MuiDataGrid-cell": {
      maxHeight: "none !important",
      whiteSpace: "normal",
    },
  },

  [`& .${classes.updateButton}`]: {
    position: "absolute",
    right: "0",
  },

  [`& .${classes.updateButtonContainer}`]: {
    marginBottom: "0.5rem",
    marginTop: "1.5rem",
    height: "40px",
    position: "relative",
  },

  [`& .${classes.pullToRefresh}`]: {
    textAlign: "center",
  },

  [`& .${classes.containerMarginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.uploadingContainer}`]: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
  },

  [`& .${classes.toggleDisplayButtons}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      position: "relative",
      marginBottom: "1rem",
    },
    [theme.breakpoints.up("sm")]: {
      position: "absolute",
      left: "0",
    },
    height: "40px",
  },

  [`& .${classes.createMyDesignButton}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.dataGridMargin}`]: {
    marginBottom: "5rem",
  },

  [`& .${classes.actionsStyle}`]: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
  },

  [`& .${classes.crmStyle}`]: {
    display: "flex",
  },

  [`& .${classes.countCrmYellow}`]: {
    color: "rgb(211, 175, 95) !important",
  },

  [`& .${classes.countCrmRed}`]: {
    color: "rgb(244, 67, 54) !important",
  },

  [`& .${classes.loadingDesigns}`]: {
    display: "flex",
    flexDirection: "column",
    height: "80px",
    justifyContent: "center",
    alignItems: "center",
    color: "rgba(255, 255, 255, 0.6)",
  },
}));

const FETCH_LIMIT = 20;

export function enrichDataWithDesignInfo(response) {
  const designsMap = response.myDesigns.reduce((acc, design) => {
    acc[design.id] = design;
    return acc;
  }, {});

  const enrichedData = response.data.map((item) => {
    const matchingDesign = designsMap[item.myDesignId];

    if (matchingDesign) {
      // Find the matching target in the targets array
      const matchingTarget = matchingDesign.targets.find(
        (target) => target.id === item.targetId
      );

      return {
        ...item,
        id: item._id,
        countCrm: matchingDesign.countCrm,
        folderId: matchingDesign.externalId,
        targetsNumber: matchingDesign.targets.length,
        label: matchingTarget ? matchingTarget.label : null, // Use target label instead of design label
        imageBorderlessUrl: matchingDesign.imageBorderlessUrl,
        imageUrl: matchingDesign.imageUrl || null, // In case imageUrl isn't present
      };
    }
    return item;
  });

  return {
    ...response,
    data: enrichedData,
  };
}

const MyDesigns = () => {
  const history = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const myDesigns = useSelector((state) => state.myDesigns.myDesigns);
  const { width } = useViewport();
  const dispatch = useDispatch();

  const [display, setDisplay] = useState("masonry");
  const [filteredMyDesigns, setFilteredMyDesigns] = useState([]);
  const [filteringDesigns, setFilteringDesigns] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const loggedIn = useSelector((state) => state.user.loggedIn);
  const loading = useSelector((state) => state.myDesigns.loading);
  const fetchLoading = useSelector((state) => state.myDesigns?.fetchLoading);
  const hasMore = useSelector((state) => state.myDesigns?.hasMore);
  const offset = useSelector((state) => state.myDesigns?.offset);
  const isSingle = useSelector((state) => state.myDesigns?.isSingle);
  const isCrmToggled = useSelector((state) => state.myDesigns?.crmEnabled);
  const openCreate = useSelector((state) => state.myDesigns?.openCreateDesign);
  const prevDisplay = usePrevious(display);

  function fetchCrmData() {
    setFilteringDesigns(true);
    crmService
      .searchCrm({})
      .then((res) => {
        if (res.data.length > 0) {
          const enrichedResponse = enrichDataWithDesignInfo(res);
          setFilteredMyDesigns(enrichedResponse.data);
        }
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      })
      .finally(() => setFilteringDesigns(false));
  }

  useEffect(() => {
    if (isCrmToggled) {
      fetchCrmData();
    }
  }, [isCrmToggled, enqueueSnackbar, t]);

  useEffect(() => {
    setFilteredMyDesigns(myDesigns);
  }, [myDesigns]);

  if (myDesigns && myDesigns.length === 1 && loggedIn && isSingle) {
    history(`/mydesign/${myDesigns[0].id}`, {
      state: {
        url:
          process.env.REACT_APP_API_URL +
          "/public/frames/" +
          myDesigns[0].id +
          "/image",
        title: myDesigns[0].label,
        myDesign: myDesigns[0],
      },
    });
  }

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const fetchDesigns = async (
    offset = 0,
    limit = FETCH_LIMIT,
    reset = false
  ) => {
    try {
      const thunk = await dispatch(fetchMyDesigns({ offset, limit, reset }));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  useEffect(() => {
    if (loggedIn) {
      fetchDesigns(0, FETCH_LIMIT, true);
    }
  }, [loggedIn, dispatch]);

  useEffect(() => {
    dispatch(closeBackdrop());
  }, []);

  function handleRefreshDesktop() {
    if (isCrmToggled) {
      fetchCrmData();
    } else {
      if (display === "masonry") {
        fetchDesigns(0, FETCH_LIMIT, true);
      } else {
        fetchDesigns("default", "default", true);
      }
    }
  }

  const handleRefreshMobile = async () => {
    await fetchDesigns(0, FETCH_LIMIT, true);
  };

  function handleDisplay(event, newDisplay) {
    if (newDisplay) setDisplay(newDisplay);
  }

  useEffect(() => {
    if (display !== prevDisplay && prevDisplay) {
      if (isCrmToggled) {
        fetchCrmData();
      } else {
        if (display === "masonry") {
          dispatch(cleanMyDesigns());
          fetchDesigns(0, FETCH_LIMIT, true);
        } else {
          fetchDesigns("default", "default", true);
        }
      }
    }
  }, [display]);

  const DisplayButton = () => (
    <StyledDiv>
      <ToggleButtonGroup
        value={display}
        exclusive
        onChange={handleDisplay}
        aria-label="zones display"
        className={classes.toggleDisplayButtons}
      >
        <ToggleButton value="masonry" aria-label="masonry">
          <ViewModule />
        </ToggleButton>
        <ToggleButton value="list" aria-label="list">
          <List />
        </ToggleButton>
      </ToggleButtonGroup>
    </StyledDiv>
  );

  function handleDownload(id) {
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    setOpenBackdrop(true);
    crmService
      .downloadReferencesFolder(id)
      .then((res) => {
        const url = window.URL.createObjectURL(res);
        a.href = url;
        a.download = `ressource_${id}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("Crm.toasts.downloadFail"), { variant: "error" });
        setOpenBackdrop(false);
      });
  }

  const handleDownloadRessource = (ressourceType, ressourceItem) => {
    if (ressourceType === "videoSimu") {
      const simuReference = ressourceItem.references.find((item) =>
        item.title.includes("simulation")
      );
      if (simuReference) {
        // Create a temporary anchor element to trigger the download
        const downloadLink = document.createElement("a");
        downloadLink.href = simuReference.url;

        // Extract filename from URL or use a default
        const fileName =
          simuReference.title ||
          simuReference.url.split("/").pop() ||
          "download";

        downloadLink.download = fileName;
        downloadLink.target = "_blank"; // Fallback for some browsers
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        enqueueSnackbar(t("Crm.toasts.noFile"), { variant: "error" });
      }
    } else handleDownload(ressourceItem._id);
  };

  const columns = (t) => [
    { field: "myDesignId", headerName: "Id", width: 88, type: "string" },
    {
      field: "folderId",
      headerName: t("myDesign.folder"),
      width: 120,
      type: "string",
    },
    {
      field: "label",
      headerName: t("myDesign.label"),
      width: 200,
      type: "string",
    },
    // {
    //   field: "targets",
    //   headerName: t("myDesign.targets"),
    //   width: 140,
    //   renderCell: (params) => (
    //     <div>
    //       {params.row.targets?.map((item, index) => {
    //         if (index === 2)
    //           return (
    //             <Typography
    //               variant="body1"
    //               key={`${item.label}-${index + 1}`}
    //               style={{ textAlign: "center" }}
    //             >
    //               ...
    //             </Typography>
    //           );
    //         else if (index <= 1)
    //           return (
    //             <Typography variant="body1" key={`${item.label}-${index + 1}`}>
    //               {item.label}
    //             </Typography>
    //           );
    //         else return null;
    //       })}
    //     </div>
    //   ),
    // },
    {
      field: "actions",
      headerName: t("myDesign.actions"),
      width: 140,
      renderCell: (params) => {
        return (
          <StyledDiv>
            <div className={classes.actionsStyle}>
              <IconButton
                color="primary"
                onClick={(e) => {
                  history(`/mydesign/${params.row.id}`);
                }}
                size="large"
              >
                <i className="fas fa-cog" style={{ color: "#D3AF5F" }} />
              </IconButton>
            </div>
          </StyledDiv>
        );
      },
    },
    {
      field: "CRM",
      headerName: "CRM",
      width: 140,
      renderCell: (params) => {
        return (
          <StyledDiv>
            <div className={classes.crmStyle}>
              <Typography
                className={
                  params.row.countCrm === params.row.targetsNumber
                    ? classes.countCrmYellow
                    : classes.countCrmRed
                }
                variant="h5"
              >
                {`${params.row.countCrm}`}
              </Typography>
              <Typography variant="h5" color="primary">
                {`\u00A0/ ${params.row.targetsNumber}`}
              </Typography>
            </div>
          </StyledDiv>
        );
      },
    },
    {
      field: "videoSimu",
      headerName: t("Crm.videoSimu"),
      width: 140,
      renderCell: (params) => {
        const videoSimuExist = params.row.references.find((item) =>
          item.title.includes("simulation")
        );
        console.log("videoSimuExist", videoSimuExist);
        return (
          <StyledDiv>
            <div className={classes.actionsStyle}>
              <IconButton
                color="primary"
                onClick={(e) => {
                  handleDownloadRessource("videoSimu", params.row);
                }}
                size="large"
                disabled={!videoSimuExist}
                sx={{
                  backgroundColor: !videoSimuExist ? "rgba(0, 0, 0, 0.26)" : "",
                }}
              >
                <DownloadIcon />
              </IconButton>
            </div>
          </StyledDiv>
        );
      },
    },
    {
      field: "References",
      headerName: t("Crm.references"),
      width: 140,
      renderCell: (params) => {
        const referencesExist = params.row.references.length > 0;
        return (
          <StyledDiv>
            <div className={classes.actionsStyle}>
              <IconButton
                color="primary"
                onClick={(e) => {
                  handleDownloadRessource("references", params.row);
                }}
                size="large"
                disabled={!referencesExist}
              >
                <DownloadIcon />
              </IconButton>
            </div>
          </StyledDiv>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Container>
        <StyledDiv>
          <div className={classes.uploadingContainer}>
            <CircularProgress size={50} />
            <Typography variant="body1">{t("loaders.fetching")}</Typography>
          </div>
        </StyledDiv>
      </Container>
    );
  } else {
    if (
      myDesigns &&
      myDesigns.length >= 0 &&
      Array.isArray(filteredMyDesigns)
    ) {
      return (
        <StyledDiv>
          <Backdrop className={classes.backdropStyle} open={openBackdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Container className={classes.containerMarginTop}>
            <ResponsiveDialogManager
              open={openCreate}
              renderer={<CreateMyDesign />}
              title={t("homePage.myDesign.createDesignModalTitle")}
              onClose={() => dispatch(closeCreateDesign())}
            />

            {width > 768 ? (
              <React.Fragment>
                <div className={classes.updateButtonContainer}>
                  {isAdmin && isCrmToggled && <DisplayButton />}
                  <Button
                    className={classes.updateButton}
                    color="primary"
                    variant="contained"
                    onClick={() => !fetchLoading && handleRefreshDesktop()}
                  >
                    {fetchLoading ? (
                      <CircularProgress size={28} />
                    ) : (
                      t("homePage.myDesign.refresh")
                    )}
                  </Button>
                </div>
                {isCrmToggled && (
                  <FiltersSearch
                    setFilteredMyDesigns={setFilteredMyDesigns}
                    setFilteringDesigns={setFilteringDesigns}
                  />
                )}
                {display === "masonry" ? (
                  <InfiniteScroll
                    dataLength={
                      isCrmToggled ? filteredMyDesigns.length : myDesigns.length
                    } //This is important field to render the next data
                    next={() =>
                      fetchDesigns(
                        parseInt(offset) + FETCH_LIMIT,
                        FETCH_LIMIT,
                        false
                      )
                    }
                    hasMore={isCrmToggled ? false : hasMore}
                    loader={
                      <div style={{ height: "100px" }}>
                        <CircularProgress size={50} />
                      </div>
                    }
                  >
                    {!filteringDesigns ? (
                      <ImageList
                        myDesigns={isCrmToggled ? filteredMyDesigns : myDesigns}
                        isCrm={isCrmToggled}
                      />
                    ) : (
                      <div className={classes.loadingDesigns}>
                        <CircularProgress size={50} />
                        <Typography>{t("loaders.fetching")}</Typography>
                      </div>
                    )}
                  </InfiniteScroll>
                ) : (
                  <div className={classes.dataGridMargin}>
                    {isAdmin && !isCrmToggled && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.createMyDesignButton}
                        startIcon={<Add />}
                        onClick={() => dispatch(openCreateDesign())}
                      >
                        {t("homePage.myDesign.addNewDesign")}
                      </Button>
                    )}
                    <DataGrid
                      rows={isCrmToggled ? filteredMyDesigns : myDesigns}
                      columns={columns(t)}
                      pageSize={10}
                      rowHeight={70}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      loading={fetchLoading}
                      classes={{
                        root: classes.root,
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                    />
                  </div>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {isAdmin && isCrmToggled && <DisplayButton />}
                {isCrmToggled && (
                  <FiltersSearch
                    setFilteredMyDesigns={setFilteredMyDesigns}
                    setFilteringDesigns={setFilteringDesigns}
                  />
                )}
                {display === "masonry" ? (
                  <InfiniteScroll
                    dataLength={
                      isCrmToggled ? filteredMyDesigns.length : myDesigns.length
                    } //This is important field to render the next data
                    next={() =>
                      fetchDesigns(
                        parseInt(offset) + FETCH_LIMIT,
                        FETCH_LIMIT,
                        false
                      )
                    }
                    hasMore={isCrmToggled ? false : hasMore}
                    loader={
                      <div style={{ height: "100px" }}>
                        <CircularProgress size={50} />
                      </div>
                    }
                    // below props only if you need pull down functionality
                    refreshFunction={handleRefreshMobile}
                    pullDownToRefresh={!isCrmToggled}
                    pullDownToRefreshThreshold={150}
                    pullDownToRefreshContent={
                      <h3 style={{ textAlign: "center" }}>
                        &#8595; {t("infiniteScroll.pullDownToRefresh")}
                      </h3>
                    }
                    releaseToRefreshContent={
                      <h3 style={{ textAlign: "center" }}>
                        &#8593; {t("infiniteScroll.releaseToRefresh")}
                      </h3>
                    }
                  >
                    {!filteringDesigns ? (
                      <ImageList
                        myDesigns={isCrmToggled ? filteredMyDesigns : myDesigns}
                        isCrm={isCrmToggled}
                      />
                    ) : (
                      <div className={classes.loadingDesigns}>
                        <CircularProgress size={50} />
                        <Typography>{t("loaders.fetching")}</Typography>
                      </div>
                    )}
                  </InfiniteScroll>
                ) : (
                  <div className={classes.dataGridMargin}>
                    {isAdmin && !isCrmToggled && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.createMyDesignButton}
                        startIcon={<Add />}
                        onClick={() => dispatch(openCreateDesign())}
                      >
                        {t("homePage.myDesign.addNewDesign")}
                      </Button>
                    )}
                    <DataGrid
                      rows={isCrmToggled ? filteredMyDesigns : myDesigns}
                      columns={columns(t)}
                      pageSize={10}
                      rowHeight={70}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      loading={fetchLoading}
                      classes={{
                        root: classes.root,
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </Container>
        </StyledDiv>
      );
    } else {
      return (
        <Container>
          <div className="loading-container">
            <Typography>{t("homePage.myDesign.noResults")}</Typography>
          </div>
        </Container>
      );
    }
  }
};

export default MyDesigns;
